import type { MoreInfoFormData } from '@iheartjane/menu';
import { MoreInfoScreen } from '@iheartjane/menu';
import { Box, Link, Loading, Modal, Typography } from '@iheartjane/reefer';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { useIsInIframe } from '@jane/shared-ecomm/hooks';
import { useJaneUser, useUpdateJaneUser } from '@jane/shared/data-access';
import { useHeadlessApp } from '@jane/shared/providers';
import { postMessageToIframeParent } from '@jane/shared/util';

import { useCustomerDispatch } from '../../../../customer/dispatch';
import { getIdentification } from '../../../../customer/redux/identification';

interface Props {
  rewardsLabel: string | null;
}

export const AddInfoPrompt = ({ rewardsLabel }: Props): ReactElement => {
  const { options } = useHeadlessApp();
  const dispatch = useCustomerDispatch();
  const isInIframe = useIsInIframe();
  const { mutate: updateJaneUser, isLoading } = useUpdateJaneUser();
  const { data: janeUser } = useJaneUser();
  const { state } = useLocation();
  const preAuthCustomer = state?.preAuthCustomer;

  const [openModal, setOpenModal] = useState(false);
  const [showError, setShowError] = useState(false);

  const renderedRewardsLabel = `Add info to see ${rewardsLabel || 'rewards'}`;

  const handleClick = () => {
    if (options?.externalAuth && options?.bloomUserExperience) {
      postMessageToIframeParent({
        messageType: 'authEvent',
        payload: {
          name: 'initiateExternalAuth',
          source: 'bag',
        },
      });
    } else {
      setOpenModal(true);
    }
  };

  const handleUpdate = ({ firstName, lastName, phone }: MoreInfoFormData) => {
    setShowError(false);
    updateJaneUser(
      {
        first_name: firstName,
        last_name: lastName,
        phone,
      },
      {
        onSuccess: () => {
          dispatch(getIdentification());
          setOpenModal(false);
        },
        onError: () => setShowError(true),
      }
    );
  };

  return (
    <Box pt={8} pb={24} pl={2}>
      <Link onClick={() => handleClick()}>{renderedRewardsLabel}</Link>
      {openModal && (
        <Modal
          variant="dialogue"
          open={openModal}
          onRequestClose={() => setOpenModal(false)}
          overlayClose
          topOverride={isInIframe ? '160px' : undefined}
        >
          <Modal.Content>
            <Box maxWidth="279px">
              {isLoading ? (
                <Box py={48} position="relative">
                  <Loading color="black" size="lg" />
                </Box>
              ) : (
                <>
                  <MoreInfoScreen
                    continueLabel="Update"
                    onSubmit={handleUpdate}
                    onDoneClick={() => setOpenModal(false)}
                    user={{
                      firstName:
                        janeUser?.user?.first_name ||
                        preAuthCustomer?.firstName,
                      lastName:
                        janeUser?.user?.last_name || preAuthCustomer?.lastName,
                      phone: janeUser?.user?.phone || undefined,
                    }}
                  />
                  {showError && (
                    <Typography mt={16} textAlign="center" color="error">
                      There was an error updating your Jane account. Try again
                      or contact{' '}
                      <Link
                        ariaLabel="link for iheartjane email"
                        href={`mailto: support@iheartjane.com`}
                        color="error"
                      >
                        support@iheartjane.com
                      </Link>{' '}
                      for more help.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Modal.Content>
        </Modal>
      )}
    </Box>
  );
};
